import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  Save,
  Loader2,
  Eye,
  Type,
  Link2,
  Tag,
  Tags,
  Upload,
  X,
  Star,
  Sparkles,
  RotateCcw,
} from "lucide-react";

import api from "../../lib/api";
import AdminLayout from "../../components/admin/AdminLayout";
import RichTextEditor from "../../components/admin/RichTextEditor";
import { RoleGate } from "../../context/AdminUserContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_HOST = process.env.REACT_APP_API_HOST || "http://127.0.0.1:8000";

// Escape plain text before injecting into the preview window markup.
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Format a date like "03 Jun 2026".
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Quill emits "<p><br></p>" for an empty editor — treat that (and any
// whitespace-only / tag-only markup) as no content.
const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<(img|iframe|video)/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim() === "";
};

// Blog categories available in the dropdown.
// const CATEGORIES = ["News", "Tech", "Events","Education"];
const CATEGORIES = ["News", "Tech", "Events","Education" , "Builder Series","Growth Series"];


// Field length standards.
const TITLE_MIN = 3;
const TITLE_MAX = 150;
const SLUG_MAX = 160;
const TAG_MAX = 25;
const TAGS_MAX = 10;

// Turn a title into a URL-friendly slug.
const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

// Tags come back from the API as a comma string (or array / JSON).
const parseTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (!tags) return [];
  try {
    const parsed = JSON.parse(tags);
    if (Array.isArray(parsed)) return parsed;
  } catch (e) {
    /* not JSON */
  }
  return String(tags)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
};

// Resolve a stored image path (e.g. "uploads/blogs/x.jpg") to a full URL.
const imageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_HOST.replace(/\/$/, "")}/${String(path).replace(/^\/?/, "")}`;
};

// Small uppercase label with a leading icon — the "Details" style.
const FieldLabel = ({ icon: Icon, children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
  >
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </label>
);

const fieldClass =
  "w-full rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition";

// Contained card with an inline title.
const Card = ({ title, children }) => (
  <section className="bg-white rounded-md border border-gray-200 shadow-sm p-5 sm:p-6">
    <h2 className="text-sm font-semibold text-gray-900 mb-5">{title}</h2>
    {children}
  </section>
);

// Coerce an API boolean (true / 1 / "1" / "true") to a real boolean.
const toBool = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

// On/off switch row.
const Toggle = ({ icon: Icon, label, description, checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className="w-full flex items-center justify-between gap-3 text-left"
  >
    <span className="flex items-start gap-2.5">
      {Icon && <Icon className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />}
      <span>
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && (
          <span className="block text-xs text-gray-500">{description}</span>
        )}
      </span>
    </span>
    <span
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-[#b32126]" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </span>
  </button>
);

const BlogPageEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);

  const [loadingBlog, setLoadingBlog] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  // Data URL for a newly chosen image; the File lives in formData.
  const [imagePreview, setImagePreview] = useState("");
  // The blog's current saved image URL (kept unless a new file is uploaded).
  const [existingImage, setExistingImage] = useState("");
  // Whether the blog is soft-deleted (drives the Restore toggle).
  const [isDeleted, setIsDeleted] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  // A featured conflict (set on a 409): { type, blog }. `type` is
  // "main_featured" or "category_featured"; `blog` is the one holding the spot.
  const [featuredConflict, setFeaturedConflict] = useState(null);
  const [resolvingConflict, setResolvingConflict] = useState(false);
  // True once the existing blog has been unset, so the user can retry Update.
  const [conflictResolved, setConflictResolved] = useState(false);
  // The blog's date, shown in the preview meta.
  const [blogDate, setBlogDate] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category: "",
    content: "",
    tags: [],
    image: null, // newly uploaded File
    imageUrl: "", // OR an external image URL — both map to blog_image
    isFeatured: false,
    isMainFeatured: false,
  });

  // Load the blog to edit.
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const { data } = await api.get(`/blogs/${id}`);
        const blog = data?.data || data?.blog || data;
        if (!blog) {
          toast.error("Blog not found");
          navigate("/admin/blogs");
          return;
        }
        setFormData({
          title: blog.blog_title || "",
          slug: blog.blog_slug || "",
          category: blog.blog_category || "",
          content: blog.blog_description || "",
          tags: parseTags(blog.blog_tags),
          image: null,
          imageUrl: "",
          isFeatured: toBool(blog.is_featured),
          isMainFeatured: toBool(blog.is_main_featured),
        });
        setExistingImage(imageUrl(blog.blog_image));
        setIsDeleted(!!blog.deleted_at);
        setBlogDate(blog.blog_date || blog.created_at || "");
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load blog"
        );
        navigate("/admin/blogs");
      } finally {
        setLoadingBlog(false);
      }
    };

    fetchBlog();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSlugChange = (e) => {
    setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // Choosing a file clears any URL — they're mutually exclusive.
      setFormData((prev) => ({ ...prev, image: file, imageUrl: "" }));
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    // Typing a URL clears any uploaded file — mutually exclusive.
    setFormData((prev) => ({ ...prev, imageUrl: url, image: null }));
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Clearing image/url drops the saved image too and brings both fields back.
  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, imageUrl: "" }));
    setImagePreview("");
    setExistingImage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addTag = (raw) => {
    const tag = raw.trim().replace(/,$/, "");
    if (!tag) return;
    if (tag.length > TAG_MAX) {
      toast.error(`Each tag must be ${TAG_MAX} characters or less`);
      return;
    }
    if (formData.tags.includes(tag)) {
      setTagInput("");
      return;
    }
    if (formData.tags.length >= TAGS_MAX) {
      toast.error(`You can add up to ${TAGS_MAX} tags`);
      return;
    }
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    setTagInput("");
  };

  const removeTag = (tag) =>
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && formData.tags.length) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Front-end validation — show one error at a time.
    const title = formData.title.trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    if (title.length < TITLE_MIN) {
      toast.error(`Title must be at least ${TITLE_MIN} characters`);
      return;
    }
    if (title.length > TITLE_MAX) {
      toast.error(`Title must be ${TITLE_MAX} characters or less`);
      return;
    }
    if (!formData.category.trim()) {
      toast.error("Category is required");
      return;
    }
    if (!formData.image && !formData.imageUrl.trim() && !existingImage) {
      toast.error("Featured image (upload or URL) is required");
      return;
    }
    if (formData.slug.length > SLUG_MAX) {
      toast.error(`Slug must be ${SLUG_MAX} characters or less`);
      return;
    }
    if (formData.tags.some((t) => t.length > TAG_MAX)) {
      toast.error(`Each tag must be ${TAG_MAX} characters or less`);
      return;
    }
    if (formData.tags.length > TAGS_MAX) {
      toast.error(`You can add up to ${TAGS_MAX} tags`);
      return;
    }

    submitBlog();
  };

  // Build the payload and send the update. Reused by the conflict modal's
  // "Update" button, so it skips re-validation (already done in handleSubmit).
  const submitBlog = async () => {
    setSaving(true);

    // Partial update — only the image is re-sent when a new file is chosen.
    const data = new FormData();
    data.append("blog_title", formData.title);
    data.append("blog_slug", formData.slug);
    data.append("blog_category", formData.category);
    if (!isEmptyHtml(formData.content)) {
      data.append("blog_description", formData.content);
    }
    formData.tags.forEach((tag) => data.append("blog_tags[]", tag));
    // Both the uploaded file and the URL save to the same blog_image field;
    // if neither is set the backend keeps the existing image.
    if (formData.image) {
      data.append("blog_image", formData.image);
    } else if (formData.imageUrl.trim()) {
      data.append("blog_image", formData.imageUrl.trim());
    }
    data.append("is_featured", formData.isFeatured ? 1 : 0);
    data.append("is_main_featured", formData.isMainFeatured ? 1 : 0);
    if (isDeleted && isRestored) data.append("is_restore", 1);

    try {
      const response = await api.post(`/blogs/${id}/update`, data);
      toast.success(response.data?.message || "Blog updated successfully");
      navigate("/admin/blogs");
    } catch (error) {
      const result = error.response?.data;
      // 409 — another blog already holds a featured spot (the single main
      // featured, or the per-category featured). Open the modal so the user
      // can unset it on the existing blog and retry.
      if (error.response?.status === 409 && result?.conflict) {
        const blog =
          result.data ||
          result.active_main_featured ||
          result.active_featured;
        setFeaturedConflict({ type: result.conflict, blog });
      } else if (result?.errors) {
        const firstError = Object.values(result.errors)[0];
        toast.error(firstError?.[0] || "Failed to update blog");
      } else {
        toast.error(result?.message || "Failed to update blog");
      }
    } finally {
      setSaving(false);
    }
  };

  // Unset is_main_featured on the blog that currently holds the spot, then
  // close the modal so the user can re-run their update.
  const removeExistingFeatured = async () => {
    if (!featuredConflict) return;
    const { type, blog } = featuredConflict;
    const field =
      type === "main_featured" ? "is_main_featured" : "is_featured";
    const label =
      type === "main_featured" ? "Feature on Blogs" : "Feature on Category";
    setResolvingConflict(true);
    try {
      const data = new FormData();
      data.append(field, 0);
      await api.post(`/blogs/${blog.id}/update`, data);
      toast.success(`Removed "${blog.blog_title}" from ${label}`);
      // Keep the modal open so the user can now hit Update.
      setConflictResolved(true);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update the existing blog"
      );
    } finally {
      setResolvingConflict(false);
    }
  };

  const closeConflictModal = () => {
    setFeaturedConflict(null);
    setConflictResolved(false);
  };

  const displayImage =
    imagePreview || formData.imageUrl.trim() || existingImage;
  const hasPreview =
    formData.title || formData.content || displayImage || formData.tags.length;

  // Open the article in a new window styled to match the public page.
  const handlePreview = () => {
    // Reuse the same named tab so re-clicking Preview refreshes it with the latest changes.
    const win = window.open("", "blogPreview");
    if (!win) {
      toast.error("Please allow pop-ups to open the preview.");
      return;
    }

    const tagsHtml = formData.tags
      .map((t) => `<span class="tag">#${escapeHtml(t)}</span>`)
      .join("");

    // Read time from the plain-text word count, like the detail page.
    const words = (formData.content || "")
      .replace(/<[^>]*>/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const readTime = `${Math.max(1, Math.round(words / 200))} min read`;

    const date = formatDate(blogDate);
    const clockIcon =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
    const calIcon =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';

    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(formData.title) || "Blog preview"}</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/ckeditor5@48.2.0/dist/ckeditor5-content.css" />
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #fff; color: #1f2937;
      font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }

    /* Hero — dark gradient, matching BlogDetailPage */
    .hero { background: linear-gradient(to bottom right, #111827, #1f2937, #111827);
      padding: 96px 24px 80px; }
    .hero-inner { max-width: 896px; margin: 0 auto; }
    .top { display: flex; align-items: center; gap: 24px; margin-bottom: 24px; }
    .back { display: inline-flex; align-items: center; gap: 8px; color: #9ca3af;
      text-decoration: none; font-size: 15px; transition: color .2s; }
    .back:hover { color: #fff; }
    .cat { display: inline-block; background: #b32126; color: #fff; font-size: 14px;
      font-weight: 600; padding: 6px 16px; border-radius: 999px; }
    .hero h1 { color: #fff; font-weight: 800; line-height: 1.1; margin: 0 0 24px;
      font-size: 48px; }
    .meta { display: flex; flex-wrap: wrap; gap: 24px; color: #d1d5db; font-size: 15px; }
    .meta span { display: inline-flex; align-items: center; gap: 8px; }

    /* Featured image — overlaps hero, same width as content */
    .image-wrap { max-width: 1152px; margin: -32px auto 0; padding: 0 24px; }
    .image-wrap img { width: 100%; height: 384px; object-fit: cover; border-radius: 16px;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); display: block; }

    /* Content */
    .content-wrap { max-width: 1152px; margin: 0 auto; padding: 64px 24px; }
    .ck-content { padding: 0; font-size: 18px; line-height: 1.8; color: #374151; }
    .ck-content h1, .ck-content h2, .ck-content h3 { color: #111827; font-weight: 700; }
    .ck-content a { color: #b32126; }
    .ck-content img { max-width: 100%; height: auto; border-radius: 12px; }
    .ck-content blockquote { border-left: 4px solid #b32126; background: #f9fafb;
      padding: 4px 20px; border-radius: 0 8px 8px 0; color: #374151; }

    /* Tags footer */
    .footer { max-width: 1152px; margin: 0 auto; padding: 0 24px 80px; }
    .tags { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 32px;
      border-top: 1px solid #e5e7eb; }
    .tag { background: #f3f4f6; color: #4b5563; font-size: 14px; padding: 4px 12px;
      border-radius: 999px; }
  </style>
</head>
<body>
  <div class="hero">
    <div class="hero-inner">
      ${formData.category ? `<div class="top"><span class="cat">${escapeHtml(formData.category)}</span></div>` : ""}
      <h1>${escapeHtml(formData.title) || "Untitled article"}</h1>
      <div class="meta">
        ${date ? `<span>${calIcon} ${date}</span>` : ""}
        <span>${clockIcon} ${readTime}</span>
      </div>
    </div>
  </div>
  ${displayImage ? `<div class="image-wrap"><img src="${displayImage}" alt="" /></div>` : ""}
  <div class="content-wrap">
    <div class="ck-content">${formData.content || ""}</div>
  </div>
  ${formData.tags.length ? `<div class="footer"><div class="tags">${tagsHtml}</div></div>` : ""}
</body>
</html>`);
    win.document.close();
  };

  const headerActions = (
    <>
      <button
        type="button"
        onClick={handlePreview}
        disabled={!hasPreview}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
      >
        <Eye className="h-4 w-4" />
        Preview
      </button>
      <button
        type="button"
        onClick={() => navigate("/admin/blogs")}
        className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="blog-form"
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#b32126] hover:bg-[#8f1a1f] shadow-sm shadow-[#b32126]/20 disabled:opacity-60 transition-colors"
      >
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        {saving ? "Updating…" : "Update Blog"}
      </button>
    </>
  );

  if (loadingBlog) {
    return (
      <AdminLayout title="Edit Blog" subtitle="Update this article">
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading blog…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Edit Blog"
      subtitle="Update this article"
      actions={headerActions}
    >
      <form id="blog-form" onSubmit={handleSubmit}>
        {/* Two equal columns */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* ---------- Left: article details ---------- */}
          <Card title="Article Details">
            <div className="space-y-5">
              {/* Title */}
              <div>
                <FieldLabel icon={Type} htmlFor="title">
                  Title
                </FieldLabel>
                <input
                  id="title"
                  type="text"
                  name="title"
                  placeholder="Enter the blog title"
                  value={formData.title}
                  onChange={handleChange}
                  maxLength={TITLE_MAX}
                  className={fieldClass}
                />
              </div>

              {/* Slug */}
              <div>
                <FieldLabel icon={Link2} htmlFor="slug">
                  Slug
                </FieldLabel>
                <div className="flex items-stretch rounded-md border border-gray-200 bg-gray-50 focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition overflow-hidden">
                  <span className="flex items-center px-3 text-sm text-gray-400 border-r border-gray-200 select-none">
                    /blogs/
                  </span>
                  <input
                    id="slug"
                    type="text"
                    name="slug"
                    placeholder="url-friendly-slug"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    maxLength={SLUG_MAX}
                    className="flex-1 bg-transparent px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <FieldLabel icon={Tag} htmlFor="category">
                  Category
                </FieldLabel>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger
                    id="category"
                    className="h-auto w-full rounded-md border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm data-[placeholder]:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15"
                  >
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Featured options — hidden for the "user" role */}
              <RoleGate hide={["user"]}>
                <div className="space-y-4 border-t border-gray-100 pt-4">
                  <Toggle
                    icon={Sparkles}
                    label="Feature on Blogs"
                    description="Show as the main featured blog on the blogs page (only one allowed)"
                    checked={formData.isMainFeatured}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        isMainFeatured: !prev.isMainFeatured,
                      }))
                    }
                  />

                  <Toggle
                    icon={Star}
                    label="Feature on Category"
                    description="Highlight within its category (one per category)"
                    checked={formData.isFeatured}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        isFeatured: !prev.isFeatured,
                      }))
                    }
                  />
                </div>
              </RoleGate>
            </div>
          </Card>

          {/* ---------- Right: image + tags ---------- */}
          <Card title="Media & Tags">
            <div className="space-y-5">
              {/* Featured image — choose from system */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />

                {/* Upload area — hidden once an image URL is entered */}
                {!formData.imageUrl.trim() &&
                  (imagePreview || existingImage ? (
                    <div className="relative group rounded-md overflow-hidden border border-gray-200">
                      <img
                        src={imagePreview || existingImage}
                        alt="preview"
                        className="h-36 w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-gray-800"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-[#b32126]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex h-36 flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-[#b32126] hover:text-[#b32126] hover:bg-[#b32126]/5 transition-colors"
                    >
                      <Upload className="h-7 w-7" />
                      <span className="text-sm font-medium">
                        Click to choose an image
                      </span>
                      <span className="text-xs">PNG, JPG up to a few MB</span>
                    </button>
                  ))}

                {/* Preview for an image URL */}
                {formData.imageUrl.trim() && (
                  <div className="relative group rounded-md overflow-hidden border border-gray-200">
                    <img
                      src={formData.imageUrl.trim()}
                      alt="preview"
                      className="h-36 w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={removeImage}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-[#b32126]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {formData.image && (
                  <p className="mt-2 truncate text-xs text-gray-500">
                    {formData.image.name}
                  </p>
                )}

                <p className="mt-2 text-xs text-gray-400">
                  Use a fixed 16:9 ratio image (e.g. 1200 × 675px) for best
                  results.
                </p>

                {/* Image URL — hidden once a file is uploaded */}
                {!formData.image && (
                  <div className="mt-3">
                    <FieldLabel icon={Link2} htmlFor="imageUrl">
                      Image URL
                    </FieldLabel>
                    <input
                      id="imageUrl"
                      type="url"
                      name="imageUrl"
                      placeholder="https://example.com/image.jpg"
                      value={formData.imageUrl}
                      onChange={handleImageUrlChange}
                      className={fieldClass}
                    />
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <FieldLabel icon={Tags} htmlFor="tags">
                  Tags
                </FieldLabel>
                <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded bg-[#b32126]/10 px-2 py-0.5 text-xs font-medium text-[#b32126]"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-[#8f1a1f]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    id="tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={() => addTag(tagInput)}
                    maxLength={TAG_MAX}
                    placeholder={formData.tags.length ? "" : "Add tags…"}
                    className="flex-1 min-w-[80px] bg-transparent py-0.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Press Enter or comma to add
                </p>
              </div>

              {/* Options */}
              {isDeleted && (
                <div className="space-y-4 border-t border-gray-100 pt-4">
                  <Toggle
                    icon={RotateCcw}
                    label="Restore"
                    description="This blog is deleted — turn on to restore it"
                    checked={isRestored}
                    onChange={() => setIsRestored((v) => !v)}
                  />
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ---------- Content: full width ---------- */}
        <div className="mt-6">
          <Card title="Content">
            <div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition">
              <RichTextEditor
                value={formData.content}
                placeholder="Write your story…"
                onChange={(html) =>
                  setFormData((prev) => ({
                    ...prev,
                    content: isEmptyHtml(html) ? "" : html,
                  }))
                }
              />
            </div>
          </Card>
        </div>

      </form>

      {/* Featured conflict modal (main featured OR per-category featured) */}
      {featuredConflict && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeConflictModal}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeConflictModal}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="border-b px-6 py-5">
              <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                {featuredConflict.type === "main_featured" ? (
                  <Sparkles className="h-5 w-5 text-[#b32126]" />
                ) : (
                  <Star className="h-5 w-5 text-[#b32126]" />
                )}
                {featuredConflict.type === "main_featured"
                  ? "Already featured on blogs"
                  : "Already featured for this category"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {featuredConflict.type === "main_featured"
                  ? "Only one blog can be featured on the blogs page. Remove it from the blog below, then update this one."
                  : `Only one blog can be featured per category${
                      featuredConflict.blog?.blog_category
                        ? ` ("${featuredConflict.blog.blog_category}")`
                        : ""
                    }. Remove it from the blog below, then update this one.`}
              </p>
            </div>

            {/* Existing featured blog */}
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                {(featuredConflict.blog?.blog_image_url ||
                  featuredConflict.blog?.blog_image) && (
                  <img
                    src={
                      featuredConflict.blog.blog_image_url ||
                      imageUrl(featuredConflict.blog.blog_image)
                    }
                    alt={featuredConflict.blog.blog_title}
                    className="h-14 w-20 shrink-0 rounded object-cover"
                  />
                )}
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900">
                    {featuredConflict.blog?.blog_title}
                  </p>
                  {featuredConflict.blog?.blog_category && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-[#b32126]/10 px-2 py-0.5 text-xs font-medium text-[#b32126]">
                      {featuredConflict.blog.blog_category}
                    </span>
                  )}
                </div>
              </div>

              {conflictResolved && (
                <p className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
                  {featuredConflict.type === "main_featured"
                    ? "Removed from Feature on Blogs. You can update this blog now."
                    : "Removed from Feature on Category. You can update this blog now."}
                </p>
              )}
            </div>

            {/* Actions — hidden once resolved; the user closes via ✕ and then
                updates this blog. */}
            {!conflictResolved && (
              <div className="flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={removeExistingFeatured}
                  disabled={resolvingConflict}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#b32126] px-4 py-2 text-sm font-semibold text-white hover:bg-[#8f1a1f] disabled:opacity-60 transition-colors"
                >
                  {resolvingConflict && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {featuredConflict.type === "main_featured"
                    ? "Remove Feature on Blogs"
                    : "Remove Feature on Category"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BlogPageEdit;